import React, { useState } from 'react';
import { uniqBy } from 'lodash';
import { ApInput } from '@alixpartners/ui-components';
import TreeSelector, {
	getCodeFromItem,
	getItemByCode,
} from 'components/TreeSelector';
import { updateCompaniesMetadata } from 'utils/apiRequests';
import { getIdealDenomination } from 'utils/number';
import {
	StyledSection as Section,
	MetadataSummary,
	MetadataSummaryLine,
} from '../ExportMetadataSection/styles';
import {
	EditGeographyButton,
	GeographyWrapper,
	Select,
	SelectWrapper,
	SummaryLabel,
	SummaryValue,
	SummaryLabelGeography,
	SummaryValueGeography,
	ValueWrapper,
} from './CompanyMetadata.styles';
import { UNITS } from '../../Settings';

const NOT_SET = 'Not set';

const getOptions = dataOptions => {
	return uniqBy(
		[
			{
				label: NOT_SET,
				value: NOT_SET,
			},
			...(dataOptions || []).map(opt => ({
				label: opt.value,
				value: opt.key,
			})),
		],
		'value'
	);
};

const getUnitOptions = () => [
	{
		label: 'None',
		value: 1,
		worth: 1,
	},
	...UNITS.map(unit => ({
		...unit,
		label: unit.value,
	})),
];

const getEmployeesUnitOptions = () => [
	{
		label: 'None',
		value: '1',
		worth: 1,
	},
	{
		label: 'Hundred',
		value: '100',
		worth: 100,
	},
	{
		label: 'Thousand',
		value: '1000',
		worth: 1000,
	},
];

const getValue = obj => {
	if (!obj || !obj.data || !obj.data.key) {
		return null;
	}
	return obj.data.key;
};

const getGeographyObjName = (data, item) => {
	const { aP_L1Code, aP_L2Code } = item;

	const foundGeography = (data || []).find(obj => {
		if (
			aP_L2Code &&
			obj.aP_L2Code === aP_L2Code &&
			obj.aP_L1Code === aP_L1Code
		) {
			return obj;
		} else if (!aP_L2Code && obj.aP_L1Code === aP_L1Code) {
			return obj;
		}
		return null;
	});
	if (foundGeography && aP_L2Code) {
		return `${foundGeography.aP_L1Category} - ${foundGeography.aP_L2Category}`;
	} else if (foundGeography && !aP_L2Code) {
		return foundGeography.aP_L1Category;
	}
	return '';
};

const currentYear = new Date().getFullYear();
const getYearRange = () => {
	const years = [];
	// Last 30 years
	for (let i = currentYear - 30; i <= currentYear; i++) {
		years.push(i);
	}
	return years.map(x => {
		return { label: x, value: x };
	});
};

export const CompanyTile = ({
	companyId,
	companyYear,
	companyAliasName,
	companyName,
	employeesNumber,
	geographies,
	headOfficeLocation,
	industry,
	revenue,
	onUpdateCompany,
	options,
	companiesMetadata,
	parsedGeographies,
	refetchCompaniesMetadata,
	refetchSpendContext,
}) => {
	const allEmployeesUnitOptions = getEmployeesUnitOptions();
	const allUnitOptions = getUnitOptions();
	const revenueIdealDenomination = revenue ? getIdealDenomination(revenue) : 1;
	const employeeIdealDenomination = Math.min(
		employeesNumber ? getIdealDenomination(employeesNumber) : 1,
		allEmployeesUnitOptions[2].worth
	);
	const unitOption = allUnitOptions.find(
		option => option.worth === revenueIdealDenomination
	);
	const employeeunitOption = allEmployeesUnitOptions.find(
		option => option.worth === employeeIdealDenomination
	);
	const [isEditGeography, setIsEditGeography] = useState(false);
	const [revenueValue, setRevenueValue] = useState(
		revenue ? revenue / revenueIdealDenomination : ''
	);
	const [revenueUnit, setRevenueUnit] = useState(unitOption.value);
	const [employeesNumberValue, setEmployeesNumber] = useState(
		employeesNumber ? employeesNumber / employeeIdealDenomination : ''
	);
	const [employeesNumberUnit, setEmployeesNumbertUnit] = useState(
		employeeunitOption.value
	);
	const [year, setYear] = useState(companyYear ? companyYear : currentYear);

	const selectedGeographies =
		geographies && geographies.data
			? geographies.data.map(item => {
					return getCodeFromItem(item).id;
			  })
			: null;

	return (
		<Section animationIndex={1} title={companyName}>
			<MetadataSummary>
				<MetadataSummaryLine>
					<SummaryLabel>Alias Name:</SummaryLabel>
					<SummaryValue>{companyAliasName}</SummaryValue>
				</MetadataSummaryLine>
				<MetadataSummaryLine>
					<SummaryLabel>Year:</SummaryLabel>
					<SummaryValue>
						<Select
							name={`${companyName}-${companyId}-year`}
							onChange={x => {
								setYear(x);
								onUpdateCompany(companyId, 'year', x);
							}}
							options={getYearRange()}
							value={year}
						/>
					</SummaryValue>
				</MetadataSummaryLine>
				<MetadataSummaryLine>
					<SummaryLabel>Industry:</SummaryLabel>
					<SummaryValue>
						<Select
							name={`${companyName}-${companyId}-industry`}
							onChange={v => {
								onUpdateCompany(companyId, 'industry', v);
							}}
							options={getOptions(options.industries)}
							value={getValue(industry)}
						/>
					</SummaryValue>
				</MetadataSummaryLine>
				<MetadataSummaryLine>
					<SummaryLabel>Revenue:</SummaryLabel>

					<SummaryValue>
						<ValueWrapper>
							<ApInput
								type="number"
								name="revenue"
								onChange={v => {
									setRevenueValue(parseFloat(v));
									onUpdateCompany(
										companyId,
										'revenue',
										parseFloat(v) * parseInt(revenueUnit)
									);
								}}
								value={revenueValue}
								shouldStopPropagation
							/>
						</ValueWrapper>
						<SelectWrapper>
							<Select
								name={`${companyName}-${companyId}-revenue`}
								small
								options={allUnitOptions}
								value={revenueUnit}
								onChange={v => {
									const foundOption = allUnitOptions.find(
										option => option.value === v
									);
									setRevenueUnit(foundOption.value);

									onUpdateCompany(
										companyId,
										'revenue',
										parseFloat(revenueValue) * parseInt(foundOption.worth)
									);
								}}
							/>
						</SelectWrapper>
					</SummaryValue>
				</MetadataSummaryLine>
				<MetadataSummaryLine>
					<SummaryLabel>Number of employees:</SummaryLabel>

					<SummaryValue>
						<ValueWrapper>
							<ApInput
								type="number"
								name="employeesNumber"
								onChange={v => {
									setEmployeesNumber(parseFloat(v));
									onUpdateCompany(
										companyId,
										'employeesNumber',
										parseFloat(v) * parseInt(employeesNumberUnit)
									);
								}}
								value={employeesNumberValue}
								shouldStopPropagation
							/>
						</ValueWrapper>
						<SelectWrapper>
							<Select
								name={`${companyName}-${companyId}-employeesNumber`}
								small
								options={allEmployeesUnitOptions}
								value={employeesNumberUnit}
								onChange={v => {
									const foundOption = allEmployeesUnitOptions.find(
										option => option.value === v
									);

									setEmployeesNumbertUnit(foundOption.value);

									onUpdateCompany(
										companyId,
										'employeesNumber',
										parseFloat(employeesNumberValue) *
											parseInt(foundOption.worth)
									);
								}}
							/>
						</SelectWrapper>
					</SummaryValue>
				</MetadataSummaryLine>
				<MetadataSummaryLine>
					<SummaryLabel>Head office location:</SummaryLabel>
					<SummaryValue>
						<Select
							name={`${companyName}-${companyId}-headOfficeLocation`}
							onChange={v => {
								onUpdateCompany(companyId, 'headOfficeLocation', v);
							}}
							options={getOptions(options.headOfficeLocations)}
							value={getValue(headOfficeLocation)}
						/>
					</SummaryValue>
				</MetadataSummaryLine>

				<MetadataSummaryLine>
					<SummaryLabelGeography>Countries operated in:</SummaryLabelGeography>
					<SummaryValueGeography>
						{geographies && geographies.data && geographies.data.length
							? geographies &&
							  geographies.data
									.map(item => getGeographyObjName(options.geographies, item))
									.join(', ')
							: NOT_SET}
						<EditGeographyButton
							iconName="outline_edit"
							onClick={() => setIsEditGeography(!isEditGeography)}
						>
							{isEditGeography ? 'Close' : 'Edit'}
						</EditGeographyButton>
					</SummaryValueGeography>
				</MetadataSummaryLine>
			</MetadataSummary>
			{isEditGeography ? (
				<GeographyWrapper>
					<TreeSelector
						name={`${companyName}-${companyId}-geographies`}
						data={parsedGeographies}
						value={selectedGeographies}
						onChange={({ target }) => {
							const selectedValues = target.value;

							const newCompanies = [...companiesMetadata];
							newCompanies[companyId].geographies = {
								data: selectedValues.map(item => {
									const obj = getItemByCode(options.geographies || [], item);
									if (!obj.aP_L2Code) {
										obj.aP_L2Code = null;
									}
									return obj;
								}),
								version: 'latest',
							};
							updateCompaniesMetadata(newCompanies).then(() => {
								refetchCompaniesMetadata().then(() => refetchSpendContext());
							});
						}}
					/>
				</GeographyWrapper>
			) : null}
		</Section>
	);
};
